/**
 * Trial utility functions for calculating trial status
 * Mirrors backend logic for consistent calculations
 */

export const TRIAL_DURATION_DAYS = 30;
export const WARNING_THRESHOLDS = [7, 3, 1]; // Days before expiration to show warnings

export interface TrialStatus {
  isOnTrial: boolean;       // true if user is on Starter plan and within trial period
  isTrialExpired: boolean;  // true if trial has expired
  trialEndDate: Date | null;
  daysRemaining: number;    // -1 if not on trial, 0+ if on trial
  showWarning: boolean;     // true if within warning threshold
}

/**
 * Calculate trial status from plan name and account creation date
 * @param planName - The user's current plan ('starter', 'pro', 'ultra')
 * @param createdAt - The user's account creation date
 * @returns TrialStatus object with trial information
 */
export function calculateTrialStatus(
  planName: string | undefined | null,
  createdAt: Date | string | null | undefined
): TrialStatus {
  // If not on starter plan, not on trial
  if (planName !== 'starter') {
    return {
      isOnTrial: false,
      isTrialExpired: false,
      trialEndDate: null,
      daysRemaining: -1,
      showWarning: false,
    };
  }

  // Starter plan with no createdAt — treat as expired (fail-closed)
  if (!createdAt) {
    return {
      isOnTrial: false,
      isTrialExpired: true,
      trialEndDate: null,
      daysRemaining: 0,
      showWarning: false,
    };
  }

  const createdDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const trialEndDate = new Date(createdDate);
  trialEndDate.setDate(trialEndDate.getDate() + TRIAL_DURATION_DAYS);

  const now = new Date();
  const msRemaining = trialEndDate.getTime() - now.getTime();
  const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

  const isTrialExpired = daysRemaining <= 0;
  const showWarning = !isTrialExpired && WARNING_THRESHOLDS.some(d => daysRemaining <= d);

  return {
    isOnTrial: !isTrialExpired,
    isTrialExpired,
    trialEndDate,
    daysRemaining: Math.max(0, daysRemaining),
    showWarning,
  };
}
